import {Component, DestroyRef, OnInit} from '@angular/core';
import {MetaData, NgEventBus} from 'ng-event-bus';
import {LookupService} from '../../common/service/lookup.service';
import {PlanModelService} from '../../common/service/plan-model.service';
import {NgClass} from '@angular/common';
import {ControlContainer, FormsModule, NgForm} from '@angular/forms';
import {NlrHasPermissionsPipe} from '../../common/pipe/nlr-has-permissions.pipe';
import {NlrPermissionsDisabledPipe} from '../../common/pipe/nlr-permissions-disabled-pipe';
import {NlrFormFieldHasErrorPipe} from '../../common/pipe/nlr-form-field-has-error.pipe';
import {TraitsService} from '../../common/service/traits.service';
import {first} from 'rxjs';
import {NLR_TABS} from '../../common/constants';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {NgSelectModule} from '@ng-select/ng-select';
import {NlrTooltipDirective} from '../../common/directive/tooltip/nlr-tooltip-directive';
import {ProjectedCoordSystemComponent} from '../projected-coord-system/projected-coord-system.component';

@Component({
    selector: 'nlr-survey-details',
    imports: [
        FormsModule,
        NgClass,
        NlrHasPermissionsPipe,
        NlrPermissionsDisabledPipe,
        NlrFormFieldHasErrorPipe,
        NgSelectModule,
        NlrTooltipDirective,
        ProjectedCoordSystemComponent
    ],
    templateUrl: './survey-details.component.html',
    styleUrl: './survey-details.component.scss',
    viewProviders: [{provide: ControlContainer, useExisting: NgForm}]
})
export class SurveyDetailsComponent implements OnInit {
    get readonly() {
        return this.planModelService.readonly;
    }

    title = 'Survey details';

    surveyMethodsList = [];
    certificationTypes = [];

    ssaValues: {displayValue: string; active: boolean; value: string}[];
    surveyMethods = [];
    informationTabActive = false;

    constructor(
        private eventBus: NgEventBus,
        private lookupService: LookupService,
        private planModelService: PlanModelService,
        private traitsService: TraitsService,
        private destroyRef: DestroyRef
    ) {}

    ngOnInit() {
        this.initialiseLookups();
        // Change surveyMethod lookup list based on fields changes
        // Note: Database should be changed as the only two checks needed are:
        // - planPurpose and ssa because planPurpose is retrieved base on tenure type and plan type
        // For now we need to check any changes to tenureType, planType, planPurpose and ssa as the
        // lookup table was written following this logic.
        this.eventBus
            .on('nlr:plan-model-updated:*')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((meta: MetaData) => {
                switch (meta.data) {
                    case 'planType':
                    case 'tenureType':
                        this.planModelService.getModel().ssa = undefined;
                        this.planModelService.getModel().surveyMethod = undefined;
                }

                if (
                    this.planModelService.getModel().tenureType === undefined ||
                    this.planModelService.getModel().planType === undefined ||
                    this.planModelService.getModel().planPurpose === undefined ||
                    this.planModelService.getModel().ssa === undefined
                ) {
                    this.surveyMethods = [];
                    this.planModelService.getModel().ssa = undefined;
                    this.planModelService.getModel().surveyMethod = undefined;
                } else {
                    this.updateSurveyMethods();
                }
            });

        this.eventBus
            .on(`nlr:tabChange:${NLR_TABS.INFORMATION}`)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.informationTabActive = true;
            });
    }

    private updateSurveyMethods() {
        this.surveyMethods = this.surveyMethodsList.filter(row => {
            const ssaBool = this.planModelService.getModel().ssa === 'true';
            return (
                row.specialSurveyArea == ssaBool &&
                row.planPurpose == this.planModelService.getModel().planPurpose.name &&
                row.tenureType == this.planModelService.getModel().tenureType.name &&
                row.planType == this.planModelService.getModel().planType.name
            );
        });
    }

    private initialiseLookups() {
        this.surveyMethodsList = this.lookupService.getLookups('surveyMethod').sort((l1, l2) => l1.referenceId - l2.referenceId);

        this.certificationTypes = this.lookupService.getLookups('certificationType');

        this.ssaValues = [
            {value: 'true', displayValue: 'Yes', active: true},
            {value: 'false', displayValue: 'No', active: true}
        ];

        if (this.getModel().planPurpose && this.getModel().tenureType && this.getModel().planType) {
            this.updateSurveyMethods();
        }
    }

    getModel() {
        return this.planModelService.getModel();
    }

    ssaChange(ssaValue) {
        this.planModelService.getModel().ssa = ssaValue;
        this.planModelService.getModel().surveyMethod = undefined;
        this.eventBus.cast('nlr:plan-model-updated:ssa', 'ssa');
    }

    surveyMethodChange(surveyMethod) {
        this.planModelService.getModel().surveyMethod = surveyMethod;
        this.updateCertificationType();
        this.updateRegulatoryStatement();
        this.eventBus.cast('nlr:plan-model-updated:surveyMethod', 'surveyMethod');

        this.traitsService
            .setPlanTraitsRequestData({surveyMethod: this.planModelService.getModel().surveyMethod.planSurveyMethod})
            .pipe(first())
            .subscribe();
    }

    surveyMethodCloseTooltip() {
        this.eventBus.cast('surveyMethod:tooltipClose');
    }

    updateCertificationType() {
        if (
            this.planModelService.getModel().surveyMethod !== undefined &&
            this.planModelService.getModel().surveyMethod.certificationlookupId !== undefined &&
            this.planModelService.getModel().surveyMethod.certificationlookupId > 0
        ) {
            this.planModelService.getModel().certificationType =
                this.certificationTypes[this.planModelService.getModel().surveyMethod.certificationlookupId - 1];

            if (this.planModelService.getModel().certificationType && this.planModelService.getModel().certificationType !== null) {
                const str = this.planModelService.getModel().certificationType.certificationText;
                this.planModelService.getModel().certification.certificationType = {
                    ...this.planModelService.getModel().certificationType
                };
                this.planModelService.getModel().certification.certificationText = str.replace(/<br>/g, '\n');
            }
        }
    }

    updateRegulatoryStatement() {
        if (this.planModelService.getModel().surveyMethod !== undefined && this.planModelService.getModel().surveyMethod.regulatoryStatementApplies) {
            this.planModelService.getModel().certification.surveyRegulatoryStatementApplies =
                this.planModelService.getModel().surveyMethod.regulatoryStatementApplies;
            if (
                this.planModelService.getModel().surveyMethod.regulatoryStatement !== undefined &&
                this.planModelService.getModel().surveyMethod.regulatoryStatement !== null
            ) {
                const str1 = this.planModelService.getModel().surveyMethod.regulatoryStatement;
                this.planModelService.getModel().certification.surveyRegulatoryStatement = str1.replace(/<br>/g, '\n');
            } else {
                this.planModelService.getModel().certification.surveyRegulatoryStatement = null;
            }
        }
    }
}
